import React, { useState, useMemo } from 'react'
import { Button } from 'react-bootstrap'
import { useToasts } from 'react-toast-notifications'
import { useHistory } from 'react-router-dom'

import { EntityList } from '../../../components/organisms/EntityList'
import { EntityCardProps } from '../../../components/organisms/EntityCard'
import { convertUnitToEntity } from '../../../domain/converters/Unit'
import { getEntitiesCount } from '../../../domain/usecases/Reports'

import { deleteUnit, getUnits, Unit } from '../../../domain/usecases/Unit'

import { filtersFactory } from '../../../main/factories/filters.factory'
import { FilterItem } from '../../../components/organisms/EntityFilter'

import { DeleteModal } from '../../../components/molecules/DeleteModal'
import { SeparateBetween } from '../../../components/templates/SeparateBetween'
import { usePermissions } from '../../../hooks/usePermissions'
import { UnitsSearch } from '../../../components/organisms/UnitsSearch'

type ListUnitsProps = {
  remoteGetUnits: getUnits
  remoteDeleteUnit: deleteUnit
  remoteGetEntitiesCount: getEntitiesCount
  remoteCreateUnit: Unit
}

const ListUnits = ({
  remoteGetUnits,
  remoteDeleteUnit,
  remoteGetEntitiesCount,
  remoteCreateUnit
}: ListUnitsProps) => {
  const history = useHistory()
  const { addToast } = useToasts()
  const { can } = usePermissions()
  const [loading, setLoading] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')

  const [units, setUnits] = useState<EntityCardProps[]>([])
  const [selectedId, setSelectedId] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const [show, setShow] = useState(false)
  const handleClose = () => setShow(false)
  const handleShow = () => setShow(true)

  const [filters, setFilters] = useState<FilterItem[]>([])

  // Filter units based on search term
  const filteredUnits = useMemo(() => {
    if (!searchTerm.trim()) {
      return units
    }

    const searchLower = searchTerm.toLowerCase()
    return units.filter((unit) => {
      // Search in unit title (name)
      if (unit.title?.toLowerCase().includes(searchLower)) {
        return true
      }

      // Search in unit subtitle (building + project info)
      if (unit.subtitle?.toLowerCase().includes(searchLower)) {
        return true
      }

      // Search in line1 and line2 (additional unit info)
      if (unit.line1?.toLowerCase().includes(searchLower)) {
        return true
      }

      if (unit.line2?.toLowerCase().includes(searchLower)) {
        return true
      }

      return false
    })
  }, [units, searchTerm])

  const handleSearchChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm)
  }

  React.useEffect(() => {
    if (errorMessage) {
      addToast(errorMessage, {
        appearance: 'error',
        autoDismiss: true
      })
    }
  }, [errorMessage])

  const requestUnits = async () => {
    setLoading(true)
    try {
      const UnitsResponse = await remoteGetUnits.getUnits()
      setUnits(
        convertUnitToEntity(
          UnitsResponse,
          can.delete('units') ? deleteUnitFunction : undefined,
          can.read('units') ? RedirectToUnitDetails : undefined,
          undefined // Photo upload removed
        )
      )
      setLoading(false)
    } catch (error) {
      setErrorMessage('Não foi possível carregar as Unidades.')
    }
  }

  React.useEffect(() => {
    requestUnits()
    remoteGetEntitiesCount
      .getEntitiesCount()
      .then((entitiesCount) =>
        setFilters(filtersFactory(entitiesCount, history))
      )
    setLoading(true)
  }, [])

  const handleDelete = () => {
    try {
      remoteDeleteUnit
        .deleteById({ id: selectedId })
        .then((res) => {
          requestUnits()
          handleClose()
        })
        .catch((error) => {
          handleClose()
          // mensagem do backend (ex.: unidade com vagas); toast direto porque o
          // errorMessage não repete o aviso quando o texto é o mesmo
          addToast(error?.message || 'Não foi possível excluir a Unidade.', {
            appearance: 'error',
            autoDismiss: true
          })
        })
    } catch (err) {
      setErrorMessage('Não foi possível excluir a Unidade.')
      return err
    }
  }

  const deleteUnitFunction = (idUnit: string) => {
    handleShow()
    setSelectedId(idUnit)
  }

  const RedirectToUnitDetails = (id: string) => {
    history.push(`/units/${id}`, { id: id })
  }

  return (
    <>
      <DeleteModal
        show={show}
        handleClose={handleClose}
        handleOk={handleDelete}
      />
      <EntityList
        title="Unidades"
        filters={filters}
        activeFilter={3}
        entityCards={filteredUnits}
        filterWithSearch={
          <UnitsSearch
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            resultsCount={filteredUnits.length}
            totalCount={units.length}
          />
        }
        buttons={
          can.create('units') ? (
            <SeparateBetween>
              <Button
                onClick={() =>
                  history.push('/units/create', {
                    complete_multiple: true
                  })
                }
                variant="info"
              >
                <strong>Completar Múltiplas Unidades</strong>
              </Button>
              <Button
                onClick={() => history.push('/units/create')}
                variant="primary"
              >
                Adicionar Unidade
              </Button>
            </SeparateBetween>
          ) : null
        }
      />
    </>
  )
}

export default ListUnits
